import _ from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Analytics from 'analytics-opencare';
import AppleWhiteLogo from 'assets/images/sso/apple-white.svg';
import AppleLogo from 'assets/images/sso/apple.svg';
import FacebookWhiteLogo from 'assets/images/sso/facebook-white.svg';
import FacebookLogo from 'assets/images/sso/facebook.svg';
import GoogleWhiteLogo from 'assets/images/sso/google-white.svg';
import GoogleLogo from 'assets/images/sso/google.svg';
import { COUNTRY } from 'common/constants';
import {
  Div,
  FlexRow,
  P,
  StyledFormItem,
} from 'common/styles/oc-styled-components';
import { colors } from 'common/styles/style-constants';
import {
  loginUser,
  logoutUser,
  updateCheckoutErrorStatus,
} from 'connect/actions';
import { CHECKOUT_ERROR_TYPES } from 'connect/constants';
import { Signup } from 'connect/saga/handlers/user/signup';
import {
  selectDataClinic,
  selectDataLoadingLogin,
  selectDataLoadingUser,
  selectDataLoginFormError,
  selectDataMatch,
  selectDataMatchToken,
  selectDataPatient,
  selectDataRequestedAppointment,
  selectDataUser,
} from 'connect/selectors';
import { Form, LogoutOutlined } from 'opencare-crown';
import PromoSmsConsentCheckbox from 'pages/legacy-checkout/components/common/PromoSmsConsentCheckbox/PromoSmsConsentCheckbox';
import {
  DIMENSIONS,
  useWindowDimensions,
} from 'providers/WindowDimensionsProvider';
import { useRedirectionService } from 'services/RedirectionService';

import CONFIG from '../../../../../../app.config';
import {
  GtmDataLayerEvent,
  trackGtmDataLayerEvent,
} from '../../../../../utils/analytics';
import FloatingInput from '../../../../common/components/FloatingInput/FloatingInput';
import ProfileImageCircles from '../../../../common/components/ProfileImageCircles/ProfileImageCircles';
import TermsAndConditions from '../../../../common/components/TermsAndConditions/TermsAndConditions';
import { getTimeFromStartTime } from '../../../../common/helpers/appointment-slot-helpers';
import {
  checkEmail,
  checkNumberString,
} from '../../../../common/helpers/form-field-helpers';
import { useFunnelStorage } from '../../../../funnel/storage/Storage';
import { StyledIcon } from '../../../checkout-styled-components';
import {
  selectAnyProviderSelected,
  selectDataCheckoutFormErrorStatus,
  selectRequestedSlot,
} from '../../../connect/selectors';
import {
  SSO_APPOINTMENT,
  SSO_PROVIDERS,
  SSO_REDIRECT,
} from '../../../helpers/checkout-constants';
import { formatPhoneNumber } from '../../../helpers/patient-form-helpers';

import {
  ForgotPasswordLink,
  HorizontalBreak,
  LoggedInBox,
  LogoutLink,
  PatientLoginButton,
  SignInUpLink,
  SSOLoginButton,
  SSOLoginButtonMobile,
  SSOLogo,
} from './styled-components';

const analytics = new Analytics();

// Component
const PatientForm = ({ handleNextStep, state, hasAccount, setHasAccount }) => {
  const dispatch = useDispatch();
  const funnelStorage = useFunnelStorage();
  const { layout } = useWindowDimensions();
  const [patientForm] = Form.useForm();

  const redirectionService = useRedirectionService();

  const loadingUser = useSelector(selectDataLoadingUser);
  const match = useSelector(selectDataMatch);
  const matchToken = useSelector(selectDataMatchToken);
  const user = useSelector(selectDataUser);
  const patient = useSelector(selectDataPatient);
  const checkoutFormErrorStatus = useSelector(
    selectDataCheckoutFormErrorStatus,
  );
  const requestedAppointment = useSelector(selectDataRequestedAppointment);
  const clinic = useSelector(selectDataClinic);
  const loginError = useSelector(selectDataLoginFormError);
  const loadingLogin = useSelector(selectDataLoadingLogin);
  const selectedSlot = useSelector(selectRequestedSlot);
  const anyProviderSelected = useSelector(selectAnyProviderSelected);
  const selectedSlotDateReadable =
    selectedSlot && moment(selectedSlot.date).format('dddd, MMM Do ');
  const selectedSlotTimeReadable =
    selectedSlot && getTimeFromStartTime(selectedSlot.time, '- h:mm a');

  const [formData, setFormData] = useState({});
  const [stepComplete, setStepComplete] = useState(false);
  const [showLoggedInBox, setShowLoggedInBox] = useState(!!patient);
  const [phoneIsBlank, setPhoneIsBlank] = useState(!state.mobilePhone);
  const [passwordIsBlank, setPasswordIsBlank] = useState(true);
  const [ssoProviderLoading, setSsoProviderLoading] = useState(null);
  const [loginFormError, setLoginFormError] = useState(false);
  const [promoSmsAllowed, setPromoSmsAllowed] = useState(null);
  const userIsClinic = (_.get(user, 'editable.clinics') || []).length > 0;

  let profileMetadata = {
    primaryPhoto: _.get(
      requestedAppointment,
      'provider.doctor.primaryPhoto',
      {},
    ),
    name: _.get(requestedAppointment, 'provider.doctor.name', ''),
  };

  if (anyProviderSelected === true) {
    profileMetadata = {
      primaryPhoto: _.get(clinic, 'primaryPhoto', {}),
      name: _.get(clinic, 'name', ''),
    };
  }

  useEffect(() => {
    if (checkoutFormErrorStatus) {
      setStepComplete(false);
    }
  }, [checkoutFormErrorStatus]);

  useEffect(() => {
    if (user && user.authenticated && patient && !hasAccount) {
      setShowLoggedInBox(true);
    }
  }, [user, patient]);

  useEffect(() => {
    if (loadingLogin === false) {
      if (loginError) {
        setLoginFormError(true);
      } else {
        setStepComplete(true);
      }
    }
  }, [loadingLogin, loginError]);

  // To move to the next step one of the following must be true:
  //   1. must be a new user who filled out the form
  //   2. or a user who is already signed in who clicks continue
  useEffect(() => {
    const canMoveToNextStep =
      stepComplete && !loadingUser || hasAccount && stepComplete;
    if (canMoveToNextStep) {
      handleNextStep(formData);
    }
  }, [patient, loadingUser, stepComplete]);

  // Reset the patient form initial values when prefill data
  // for the email and phone values exist.
  useEffect(() => {
    if (
      funnelStorage.prefillData.patientEmail
      || funnelStorage.prefillData.patientPhone
    ) {
      patientForm.resetFields();

      if (funnelStorage.prefillData.patientPhone) setPhoneIsBlank(false);
    }
  }, [funnelStorage.prefillData]);

  const handleLogout = () => {
    analytics.clearUserId();
    dispatch(logoutUser());
    dispatch(updateCheckoutErrorStatus(null));

    patientForm.setFieldsValue({
      email: null,
      mobilePhone: null,
    });

    setPhoneIsBlank(true);
    setPasswordIsBlank(true);
    setShowLoggedInBox(false);
  };

  const handleFormChange = () => {
    const fields = patientForm.getFieldsValue();
    setLoginFormError(false);

    if (fields.password) setPasswordIsBlank(false);
    else setPasswordIsBlank(true);

    if (Object.values(fields).some(inputText => !inputText)) {
      setPhoneIsBlank(true);
    } else {
      setPhoneIsBlank(false);
    }
  };

  const handleFormSubmit = values => {
    // if logged in, ignore validating anything
    if (showLoggedInBox) {
      trackGtmDataLayerEvent(
        GtmDataLayerEvent.APPOINTMENT_CHECKOUT_ACCOUNT_CREATED,
      );
      analytics.trackClick({
        pageType: analytics.pageTypes.FORM,
        pageName: 'form-booking-account',
        name: 'form-booking',
        section: [
          SSO_PROVIDERS.GOOGLE,
          SSO_PROVIDERS.FACEBOOK,
          SSO_PROVIDERS.APPLE,
        ].includes(user.authStrategy)
          ? 'sso-confirm-login'
          : 'confirm-login',
        content: {
          confirm: 'true',
        },
        practiceId: requestedAppointment.provider?.clinic,
        providerId: requestedAppointment.providerId,
      });
      setStepComplete(true);
      return;
    }

    if (hasAccount) {
      dispatch(loginUser(values.email, values.password));
      return;
    }

    // Validate manual form
    if (!patient) {
      const patientDetails = {
        mobilePhone: `+1${values.mobilePhone}`,
        email: values.email,
      };
      if (typeof promoSmsAllowed === 'boolean') {
        patientDetails.settings = {
          promoSmsAllowed,
        };
      }
      // Make sure we don't dispatch the action multiple times
      if (loadingLogin || loadingUser) {
        return;
      }
      dispatch(Signup.createAction(patientDetails, match, matchToken));
    }

    trackGtmDataLayerEvent(
      GtmDataLayerEvent.APPOINTMENT_CHECKOUT_ACCOUNT_CREATED,
    );
    analytics.trackForm({
      pageType: analytics.pageTypes.FORM,
      pageName: 'form-booking-account',
      name: 'form-booking',
      section: 'account',
      content: {
        email: values.email,
        phone: values.mobilePhone.toString(),
      },
      practiceId: requestedAppointment.provider?.clinic,
      providerId: requestedAppointment.providerId,
    });

    if (checkoutFormErrorStatus === null && !loadingUser) {
      setStepComplete(true);
    }

    setFormData(values);
  };

  const handleEmailChange = () => {
    if (
      [
        CHECKOUT_ERROR_TYPES.EMAIL_ALREADY_REGISTERED,
        CHECKOUT_ERROR_TYPES.EMAIL_INVALID,
      ].includes(checkoutFormErrorStatus)
    ) {
      dispatch(updateCheckoutErrorStatus(null));
    }
  };

  const handlePhoneChange = () => {
    if (checkoutFormErrorStatus === CHECKOUT_ERROR_TYPES.PHONE_INVALID) {
      dispatch(updateCheckoutErrorStatus(null));
    }
  };

  const loginWithSSO = ssoProvider => {
    if (!ssoProviderLoading) {
      setSsoProviderLoading(ssoProvider);

      trackGtmDataLayerEvent(
        GtmDataLayerEvent.APPOINTMENT_CHECKOUT_ACCOUNT_CREATED,
      );
      analytics.trackClick({
        pageType: analytics.pageTypes.FORM,
        pageName: 'form-booking-account',
        name: 'form-booking',
        section: 'account',
        content: {
          [`sso-${ssoProvider}`]: 'true',
          ssoProviderName: ssoProvider,
        },
        practiceId: requestedAppointment.provider?.clinic,
        providerId: requestedAppointment.providerId,
        funnelConfigId: _.get(funnelStorage, 'config.id'),
      });

      analytics.waitForQueue().then(() => {
        // Set the query params (SSO redirect, requested appointment, if any provider was selected)
        let queryParams = `?${SSO_REDIRECT}=true`;
        queryParams += `&${SSO_APPOINTMENT.PROVIDER}=${
          requestedAppointment.providerId
        }`;

        if (requestedAppointment.slotId) {
          queryParams += `&${SSO_APPOINTMENT.SLOT}=${
            requestedAppointment.slotId
          }`;
        }

        const anyProviderSelectedString =
          anyProviderSelected === undefined || anyProviderSelected === null
            ? ''
            : anyProviderSelected.toString();
        queryParams += `&${
          SSO_APPOINTMENT.ANY_PROVIDER
        }=${anyProviderSelectedString}`;

        // Generate redirect URL
        const redirectURI = encodeURIComponent(
          `${window.location.href.split('?')[0]}${queryParams}`,
        );

        const ssoUrl = `${
          CONFIG.API_URL
        }/auth/${ssoProvider}?redirectUri=${redirectURI}`;

        // Send them on their way
        setSsoProviderLoading(null);
        redirectionService.redirect({
          url: ssoUrl,
          reason: `sso-${ssoProvider}`,
        });
      });
    }
  };

  const loginWithGoogle = () => {
    loginWithSSO(SSO_PROVIDERS.GOOGLE);
  };

  const loginWithFacebook = () => {
    loginWithSSO(SSO_PROVIDERS.FACEBOOK);
  };

  const loginWithApple = () => {
    loginWithSSO(SSO_PROVIDERS.APPLE);
  };

  const renderLoginCopy = () => {
    if (patient) {
      return 'Continue';
    }

    return hasAccount ? 'Log in' : 'Continue Booking';
  };

  const renderSSO = () => {
    if (layout === DIMENSIONS.MOBILE)
      return (
        <>
          <P fontSize="17px" fontWeight="500">
            Continue using your account:
          </P>
          <FlexRow justify="space-between" align="center">
            <SSOLoginButtonMobile
              type="button"
              data-testid="ssoLoginGoogle"
              disabled={userIsClinic}
              onClick={loginWithGoogle}
              background={colors.google}
            >
              {ssoProviderLoading === SSO_PROVIDERS.GOOGLE
                ? (
                  <StyledIcon
                    type="loading"
                    fontSize="20px"
                    margin="0 20px 0 0"
                  />
                )
                : (
                  <SSOLogo
                    src={GoogleWhiteLogo}
                    isMobile
                    alt=""
                    role="presentation"
                  />
                )
              }
            </SSOLoginButtonMobile>
            <SSOLoginButtonMobile
              type="button"
              data-testid="ssoLoginFacebook"
              disabled={userIsClinic}
              onClick={loginWithFacebook}
              background={colors.facebook}
            >
              {ssoProviderLoading === SSO_PROVIDERS.FACEBOOK
                ? (
                  <StyledIcon
                    type="loading"
                    fontSize="20px"
                    margin="0 20px 0 0"
                  />
                )
                : (
                  <SSOLogo
                    src={FacebookWhiteLogo}
                    height="22px"
                    isMobile
                    alt=""
                    role="presentation"
                  />
                )
              }
            </SSOLoginButtonMobile>
            <SSOLoginButtonMobile
              type="button"
              data-testid="ssoLoginApple"
              disabled={userIsClinic}
              onClick={loginWithApple}
              background={colors.black}
            >
              {ssoProviderLoading === SSO_PROVIDERS.APPLE
                ? (
                  <StyledIcon
                    type="loading"
                    fontSize="20px"
                    margin="0 20px 0 0"
                  />
                )
                : (
                  <SSOLogo
                    src={AppleWhiteLogo}
                    isMobile
                    alt=""
                    role="presentation"
                  />
                )
              }
            </SSOLoginButtonMobile>
          </FlexRow>
        </>
      );

    return (
      <>
        <SSOLoginButton
          type="button"
          data-testid="ssoLoginGoogle"
          disabled={userIsClinic}
          onClick={loginWithGoogle}
        >
          {ssoProviderLoading === SSO_PROVIDERS.GOOGLE
            ? (
              <StyledIcon
                type="loading"
                fontSize="20px"
                margin="0 20px 0 0"
                color={colors.white}
              />
            )
            : <SSOLogo src={GoogleLogo} alt="" role="presentation" />
          }
          Continue with Google
        </SSOLoginButton>
        <SSOLoginButton
          type="button"
          data-testid="ssoLoginFacebook"
          disabled={userIsClinic}
          onClick={loginWithFacebook}
        >
          {ssoProviderLoading === SSO_PROVIDERS.FACEBOOK
            ? <StyledIcon type="loading" fontSize="20px" margin="0 20px 0 0" />
            : <SSOLogo src={FacebookLogo} alt="" role="presentation" />
          }
          Continue with Facebook
        </SSOLoginButton>
        <SSOLoginButton
          type="button"
          data-testid="ssoLoginApple"
          disabled={userIsClinic}
          onClick={loginWithApple}
        >
          {ssoProviderLoading === SSO_PROVIDERS.APPLE
            ? <StyledIcon type="loading" fontSize="20px" margin="0 20px 0 0" />
            : <SSOLogo src={AppleLogo} alt="" role="presentation" />
          }
          Continue with Apple
        </SSOLoginButton>
      </>
    );
  };

  const renderCTA = () => (
    <StyledFormItem marginbottom="5px" margintop="20px">
      <PatientLoginButton
        type="primary"
        shape="round"
        size="large"
        block
        className="patient-form-button"
        data-testid="patientFormButton"
        htmlType="submit"
        loading={Boolean(loadingLogin || loadingUser)}
        disabled={
          checkoutFormErrorStatus && !hasAccount
          || phoneIsBlank && !user?.authenticated
          || hasAccount && passwordIsBlank && !user?.authenticated
          || loadingLogin
          || loadingUser
        }
      >
        {renderLoginCopy()}
      </PatientLoginButton>
    </StyledFormItem>
  );

  const renderForm = () => (
    <>
      <FloatingInput
        field="email"
        type="email"
        form={patientForm}
        label="Email"
        required
        capitalize={false}
        height="100px"
        error={
          checkoutFormErrorStatus
          === CHECKOUT_ERROR_TYPES.EMAIL_ALREADY_REGISTERED
        }
        onChange={handleEmailChange}
        testId="email"
        className="sensitive-data"
        disabled={
          checkoutFormErrorStatus
          === CHECKOUT_ERROR_TYPES.LOGGED_IN_AS_NOT_PATIENT
          || loadingLogin
          || loadingUser
        }
        validator={checkEmail}
        initialValue={
          patient ? state.email : funnelStorage.prefillData.patientEmail
        }
      />
      {hasAccount
        ? (
          <>
            <FloatingInput
              field="password"
              htmlType="password"
              form={patientForm}
              label="Password"
              required
              capitalize={false}
              testId="password"
              className="sensitive-data"
              disabled={
                checkoutFormErrorStatus
                === CHECKOUT_ERROR_TYPES.LOGGED_IN_AS_NOT_PATIENT || loadingLogin
              }
            />
            {loginFormError
              && (
                <P
                  color={colors.danger}
                  fontWeight="bold"
                  textAlign="center"
                  margin="-5px 0 5px"
                >
                  Invalid email or password
                </P>
              )
            }
            <a
              href={`${CONFIG.PATIENT_APP_URL}/#/forgot-password/`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <ForgotPasswordLink>Forgot your password?</ForgotPasswordLink>
            </a>
          </>
        )
        : (
          <div>
            <FloatingInput
              field="mobilePhone"
              htmlType="tel"
              form={patientForm}
              label="Phone Number"
              onChange={handlePhoneChange}
              required
              validator={checkNumberString}
              capitalize={false}
              testId="phoneNumber"
              className="sensitive-data"
              disabled={
                checkoutFormErrorStatus
                === CHECKOUT_ERROR_TYPES.LOGGED_IN_AS_NOT_PATIENT || loadingUser
              }
              initialValue={formatPhoneNumber(
                patient
                  ? state.mobilePhone
                  : funnelStorage.prefillData.patientPhone,
              )}
            />
            {checkoutFormErrorStatus === CHECKOUT_ERROR_TYPES.PHONE_INVALID
              && (
                <Div
                  margin="-12px 0 10px"
                  className="ant-form-item-explain ant-form-item-explain-error"
                >
                  <div role="alert">
                    The phone number is invalid, please check it and try again.
                  </div>
                </Div>
              )
            }
            {clinic?.address?.country === COUNTRY.unitedStates
              && (
                <PromoSmsConsentCheckbox
                  onChange={setPromoSmsAllowed}
                  disabled={loadingUser}
                />
              )
            }
          </div>
        )
      }
      <P fontWeight="bold" textAlign="center" margin="15px 0 0">
        {hasAccount ? 'First time booking? ' : 'Already have an account? '}
        <SignInUpLink onClick={() => setHasAccount(!hasAccount)}>
          {hasAccount ? 'Sign Up' : 'Log in'}
        </SignInUpLink>
      </P>
      {renderCTA()}
      <HorizontalBreak margin="0 0 10px">or</HorizontalBreak>
      {renderSSO()}
    </>
  );

  return (
    <Form
      form={patientForm}
      className="patient-form"
      layout="vertical"
      name="basic"
      onFinish={handleFormSubmit}
      data-testid="form"
      fadeform={userIsClinic ? 'true' : 'false'}
      onChange={handleFormChange}
    >
      {requestedAppointment
        && (
          <>
            <FlexRow
              aria-label="Provider"
              align="center"
              margin={layout === DIMENSIONS.MOBILE ? '-5px 0 15px' : '0 0 20px'}
            >
              <ProfileImageCircles
                height="50px"
                images={[
                  {
                    ...profileMetadata.primaryPhoto,
                    altText: profileMetadata.name,
                  },
                ]}
                width="50px"
              />
              <Div margin="0 0 0 15px">
                <P fontWeight="bold">{profileMetadata.name}</P>
                {selectedSlot && requestedAppointment.slotId
                  ? <P>{selectedSlotDateReadable + selectedSlotTimeReadable}</P>
                  : <P>Appointment Request</P>
                }
                <P>{`${_.get(clinic, 'address.street_number', '')} ${_.get(
                  clinic,
                  'address.route',
                  '',
                )}, ${_.get(clinic, 'address.locality', '')} ${_.get(
                  clinic,
                  'address.administrative_area_level_1_short',
                  '',
                )}, ${_.get(clinic, 'address.postal_code', '')}`}
                </P>
              </Div>
            </FlexRow>
          </>
        )
      }
      {showLoggedInBox
        ? (
          <>
            <LoggedInBox
              data-testid="loggedInBox"
              padding="15px"
              margin="0 0 10px 0"
              aria-label="Logged in user"
            >
              <FlexRow align="center">
                {user.authStrategy === SSO_PROVIDERS.GOOGLE
                  && (
                    <SSOLogo
                      src={GoogleLogo}
                      alt=""
                      role="presentation"
                      data-testid="sso-logo-google"
                    />
                  )
                }
                {user.authStrategy === SSO_PROVIDERS.FACEBOOK
                  && (
                    <SSOLogo
                      src={FacebookLogo}
                      alt=""
                      role="presentation"
                      data-testid="sso-logo-facebook"
                    />
                  )
                }
                {user.authStrategy === SSO_PROVIDERS.APPLE
                  && (
                    <SSOLogo
                      src={AppleLogo}
                      alt=""
                      role="presentation"
                      data-testid="sso-logo-apple"
                    />
                  )
                }
                <div>
                  <P fontSize="17px" fontWeight="500">
                    {state.firstName}{' '}
                    <span className="sensitive-data">{state.lastName}</span>
                  </P>
                  <P>{state.email}</P>
                </div>
              </FlexRow>
              <LogoutLink onClick={handleLogout} type="link">
                <LogoutOutlined aria-hidden />
                Log out, this isn't me
              </LogoutLink>
            </LoggedInBox>
            {renderCTA()}
          </>
        )
        : renderForm()
      }
      {!showLoggedInBox
        && (
          <Div margin="15px 0 0">
            <TermsAndConditions />
          </Div>
        )
      }
    </Form>
  );
};

PatientForm.propTypes = {
  handleNextStep: PropTypes.func,
  state: PropTypes.object,
  hasAccount: PropTypes.bool,
  setHasAccount: PropTypes.func,
};

export default PatientForm;
